/**
 * The menu, as one document.
 *
 * A kiosk fetches this once and caches it; add-ons and change options arrive
 * *nested* even though they are rows on the server, because the alternative is
 * three round trips to render one product modal. That is a serialisation choice
 * at the edge and says nothing about storage — `docs/data-model.md` argues the
 * other half.
 */
import { z } from 'zod'
import { zId, zMoney, zSku } from './scalars'

export const zAddOn = z.object({
  code: zSku,
  label: z.string(),
  price: zMoney,
  unit: z.string().optional(),
  pieces: z.number().int().optional(),
  group: z.enum(['shots', 'extras'])
})

export const zChangeOption = z.object({ code: zSku, label: z.string(), price: zMoney })

/**
 * A section of the rail.
 *
 * `id` is a bare slug now — `coffee`, `ade-juice-tea` — and no longer
 * `<branchId>:<slug>`: the rail belongs to the chain, and the shop it is served
 * to is already on the document. Nothing in the page parses an id, so the
 * change is invisible there; it is written down here because the *meaning*
 * moved even though the shape did not.
 */
export const zCategory = z.object({
  id: zId,
  name: z.string(),
  nameEn: z.string().optional(),
  sortOrder: z.number().int(),
  imageUrl: z.string().optional()
})

export const zProduct = z.object({
  id: zSku,
  sku: zSku,
  name: z.string(),
  nameEn: z.string().optional(),
  categoryId: zId,
  imageUrl: z.string().optional(),
  price: zMoney,
  discountedPrice: zMoney.nullable(),
  balance: z.number().int(),
  isInfinite: z.boolean(),
  onlyTakeIn: z.boolean(),
  isHot: z.boolean(),
  iceSku: zSku.optional(),
  iceName: z.string().optional(),
  /** The iced SKU's own picture, resolved by the server. See `imageFor`. */
  iceImageUrl: z.string().optional(),
  addOns: z.array(zAddOn),
  changeOptions: z.array(zChangeOption),
  enabled: z.boolean()
})

export const zMenu = z.object({
  /**
   * Whose menu this is.
   *
   * On the wire so the kiosk can tell that a cached menu belongs to the branch
   * it is currently enrolled against. Harmless while the catalogue was global;
   * a wrong-price bug the moment it is not, because the IndexedDB cache is
   * stored under one key and survives a re-enrolment to a different shop.
   */
  branchId: zId,
  categories: z.array(zCategory),
  products: z.array(zProduct),
  /** When the server built it. The kiosk shows a staleness strip past a threshold. */
  servedAt: z.string(),
  /** 1C's `isclosed`. The kiosk refuses to sell. */
  branchClosed: z.boolean(),
  /** Changes whenever the catalogue does. The kiosk compares before refetching. */
  revision: z.string()
})

export type MenuWire = z.infer<typeof zMenu>
export type ProductWire = z.infer<typeof zProduct>
